import { Component, computed, effect, inject, input, output } from '@angular/core';
import { CompanyUsersService } from '../../../core/services/company-users.service';
import { I18nService } from '../../../core/i18n';
import { TenantService } from '../../../core/tenant/tenant.service';
import { isGuidString } from '../../../core/utils/guid.util';
import { type SelectItem } from '../select/select.component';
import { AppComboboxComponent, type ComboboxOptionGroup } from '../combobox/app-combobox.component';
import { UserAvatarComponent } from '../user-avatar/user-avatar.component';

@Component({
  selector: 'app-person-guid-picker',
  standalone: true,
  imports: [AppComboboxComponent, UserAvatarComponent],
  template: `
    <div class="pgp" [class.pgp--inline]="!showLabel()">
      @if (showLabel() && label()) {
        <span class="pgp__label">{{ label() }}</span>
      }

      @if (!activeCompanyId()) {
        <div class="pgp__readonly">{{ t('companyUsers.noCompanyContext') }}</div>
      } @else if (readOnly()) {
        <div class="pgp__readonly pgp__readonly--avatar">
          @if (readOnlyPersonId(); as pid) {
            <app-user-avatar [userId]="pid" [label]="readOnlyPersonLabel()" [size]="avatarSizePx()" />
            <span class="pgp__readonly-name">{{ readOnlyPersonLabel() }}</span>
          } @else if (readOnlyPlainText(); as plain) {
            <span>{{ plain }}</span>
          } @else {
            <span class="pgp__readonly-empty">—</span>
          }
        </div>
      } @else if (loading()) {
        <div class="pgp__skeleton" aria-busy="true"></div>
      } @else if (error()) {
        <div class="pgp__error-block">
          <p class="pgp__error">{{ errorMessage() }}</p>
          <button type="button" class="pgp__retry" (click)="onRetry()">
            {{ t('common.retry') }}
          </button>
        </div>
      } @else {
        <app-combobox
          [label]="''"
          [personLayout]="true"
          [optionGroups]="personPickerGroups()"
          [options]="personPickerFlatOptions()"
          [value]="currentValue()"
          [searchPlaceholder]="t('companyUsers.searchPlaceholder')"
          [emptyLabel]="t('companyUsers.emptyFilter')"
          [closedPlaceholder]="closedPlaceholder()"
          [variant]="'select'"
          [fixedPanel]="true"
          [avatarSizePx]="avatarSizePx()"
          [disabled]="false"
          (valueChange)="onPick($event)"
        />
        @if (showOrphanBanner()) {
          <div class="pgp__orphan">
            <p class="pgp__orphan-text">{{ t('companyUsers.orphanGuidWarning') }}</p>
            @if (allowClear()) {
              <button type="button" class="pgp__orphan-clear" (click)="clearValue()">
                {{ t('companyUsers.clearInvalidUser') }}
              </button>
            }
          </div>
        }
      }
    </div>
  `,
  styles: [
    `
      .pgp {
        display: grid;
        gap: 6px;
        min-width: 0;
      }

      .pgp--inline {
        gap: 0;
      }

      .pgp--inline .pgp__readonly {
        min-height: 30px;
        padding: 0 8px;
        border-radius: 8px;
        font-size: 12px;
      }

      .pgp--inline .pgp__readonly-name {
        font-size: 12px;
      }

      .pgp__label {
        font-size: 12px;
        color: var(--faint);
      }

      .pgp__readonly {
        min-height: 36px;
        display: flex;
        align-items: center;
        padding: 0 10px;
        border: 1px solid var(--border);
        border-radius: 10px;
        background: color-mix(in srgb, var(--muted) 6%, var(--bg-surface));
        color: var(--text);
        font-size: 13px;
      }

      .pgp__readonly--avatar {
        gap: 10px;
      }

      .pgp__readonly-name {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .pgp__readonly--avatar {
        gap: 10px;
      }

      .pgp__readonly-name {
        min-width: 0;
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-weight: 600;
      }

      .pgp__readonly-empty {
        color: var(--muted);
      }

      .pgp__skeleton {
        height: 36px;
        border-radius: 10px;
        background: linear-gradient(
          90deg,
          color-mix(in srgb, var(--muted) 12%, transparent) 0%,
          color-mix(in srgb, var(--muted) 22%, transparent) 50%,
          color-mix(in srgb, var(--muted) 12%, transparent) 100%
        );
        background-size: 200% 100%;
        animation: pgp-shimmer 1.2s ease-in-out infinite;
      }

      @keyframes pgp-shimmer {
        0% {
          background-position: 100% 0;
        }
        100% {
          background-position: -100% 0;
        }
      }

      .pgp__error-block {
        display: grid;
        gap: 8px;
        padding: 8px 10px;
        border: 1px solid color-mix(in srgb, var(--danger, #dc2626) 35%, var(--border));
        border-radius: 10px;
        background: color-mix(in srgb, var(--danger, #dc2626) 6%, var(--bg-surface));
      }

      .pgp__error {
        margin: 0;
        font-size: 12px;
        line-height: 1.35;
        color: var(--danger, #b91c1c);
      }

      .pgp__retry {
        justify-self: start;
        height: 30px;
        padding: 0 12px;
        font-size: 12px;
        font-weight: 600;
        border-radius: 8px;
        border: 1px solid var(--border);
        background: var(--bg-surface);
        color: var(--text);
        cursor: pointer;
      }

      .pgp__retry:hover {
        border-color: color-mix(in srgb, var(--text) 14%, var(--border));
      }

      .pgp__orphan {
        display: grid;
        gap: 8px;
        padding: 8px 10px;
        border-radius: 10px;
        border: 1px solid color-mix(in srgb, #d97706 40%, var(--border));
        background: color-mix(in srgb, #fef3c7 55%, var(--bg-surface));
      }

      .pgp__orphan-text {
        margin: 0;
        font-size: 12px;
        line-height: 1.4;
        color: var(--text);
      }

      .pgp__orphan-clear {
        justify-self: start;
        height: 30px;
        padding: 0 12px;
        font-size: 12px;
        font-weight: 600;
        border-radius: 8px;
        border: 1px solid var(--border);
        background: var(--bg-surface);
        color: var(--text);
        cursor: pointer;
      }

      .pgp__orphan-clear:hover {
        border-color: color-mix(in srgb, var(--text) 14%, var(--border));
      }
    `,
  ],
})
export class PersonGuidPickerComponent {
  private readonly usersApi = inject(CompanyUsersService);
  private readonly i18n = inject(I18nService);
  private readonly tenant = inject(TenantService);

  readonly t = this.i18n.t.bind(this.i18n);

  /** Rótulo do campo (ex.: nome do custom field). */
  readonly label = input<string>('');
  readonly showLabel = input<boolean>(true);

  /** Avatar / combobox no modo lista (sem rótulo acima): mais baixo para alinhar à linha. */
  readonly avatarSizePx = computed(() => (this.showLabel() ? 28 : 24));
  readonly required = input<boolean>(false);
  /** Valor persistido: GUID ou vazio. */
  readonly value = input<string | null>(null);
  readonly valueChange = output<string | null>();
  readonly readOnly = input<boolean>(false);
  /** Permite opção vazia / limpar (campo opcional). */
  readonly allowClear = input<boolean>(false);

  constructor() {
    effect(() => {
      if (!this.tenant.companyId()) return;
      void this.usersApi.ensureLoaded();
    });
  }

  readonly activeCompanyId = computed(() => this.tenant.companyId());

  readonly loading = computed(() => {
    if (!this.tenant.companyId()) return false;
    if (this.usersApi.loading()) return true;
    return !this.usersApi.hasDataForActiveCompany() && !this.usersApi.error();
  });

  readonly error = computed(() => !!this.usersApi.error());

  readonly errorMessage = computed(() => {
    const raw = this.usersApi.error();
    if (!raw) return '';
    if (raw === '__company_users_load_failed__') return this.t('companyUsers.loadError');
    return raw;
  });

  readonly closedPlaceholder = computed(() =>
    this.allowClear()
      ? this.t('companyUsers.none')
      : this.t('companyUsers.selectPlaceholder')
  );

  readonly baseUserOptions = computed<SelectItem[]>(() =>
    this.usersApi.users().map((u) => ({
      value: u.id,
      label: u.name,
      meta: u.email || undefined,
      userId: u.id,
    }))
  );

  readonly orphanId = computed(() => {
    const v = (this.value() ?? '').trim();
    if (!v || !isGuidString(v)) return '';
    const inList = this.usersApi.users().some((u) => u.id === v);
    return inList ? '' : v;
  });

  readonly showOrphanBanner = computed(() => {
    const o = this.orphanId();
    if (!o) return false;
    return this.currentValue() === o;
  });

  readonly personPickerGroups = computed((): ComboboxOptionGroup[] => {
    const clear: SelectItem[] = this.allowClear()
      ? [{ value: '', label: this.t('companyUsers.none'), userId: null }]
      : [];
    const orphan = this.orphanId();
    const orphanOpts: SelectItem[] = orphan
      ? [
          {
            value: orphan,
            label: this.t('companyUsers.userNotFound'),
            meta: orphan,
            userId: orphan,
          },
        ]
      : [];
    const people = this.baseUserOptions();
    const firstOpts = [...clear, ...orphanOpts];
    const groups: ComboboxOptionGroup[] = [];
    if (firstOpts.length) {
      groups.push({
        heading: this.t('companyUsers.sectionAssignees', undefined, 'Responsáveis'),
        options: firstOpts,
      });
    }
    groups.push({
      heading: this.t('companyUsers.sectionPeople', undefined, 'Pessoas'),
      options: people,
    });
    return groups;
  });

  readonly personPickerFlatOptions = computed<SelectItem[]>(() => {
    const seen = new Set<string>();
    const out: SelectItem[] = [];
    for (const g of this.personPickerGroups()) {
      for (const o of g.options) {
        if (seen.has(o.value)) continue;
        seen.add(o.value);
        out.push(o);
      }
    }
    return out;
  });

  currentValue(): string {
    return (this.value() ?? '').trim();
  }

  readOnlyPersonId(): string | null {
    const v = this.currentValue();
    return v && isGuidString(v) ? v : null;
  }

  readOnlyPersonLabel(): string {
    const id = this.readOnlyPersonId();
    if (!id) return '';
    const name = this.usersApi.resolveUserDisplayName(id).trim();
    if (name && name !== id) return name;
    return this.t('companyUsers.userNotFound');
  }

  /** Valor só texto (não GUID) em modo leitura. */
  readOnlyPlainText(): string | null {
    const v = this.currentValue();
    if (!v) return null;
    if (isGuidString(v)) return null;
    return v;
  }

  readonly readOnlyDisplay = computed(() => {
    const v = (this.value() ?? '').trim();
    if (!v) return '—';
    const hit = this.usersApi.users().find((u) => u.id === v);
    if (hit) {
      return hit.email ? `${hit.name} · ${hit.email}` : hit.name;
    }
    if (isGuidString(v)) {
      return `${this.t('companyUsers.userNotFound')} (${v})`;
    }
    return v;
  });

  onPick(v: string | null): void {
    this.valueChange.emit(v && v.length ? v : null);
  }

  onRetry(): void {
    void this.usersApi.retry();
  }

  clearValue(): void {
    this.valueChange.emit(null);
  }
}
